import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'sharedUi/Toast';
import { Tag } from 'sharedUi/Tag';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'sharedUi/Button';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { usersService, type GroupedUser, type GroupedUserProject, type UserQueryParams } from '../../services/users.service';
import { useDebounce } from '../../hooks/useDebounce';
import { personnelLookupService, type PersonnelMinimal } from '../../services/personnel-lookup.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';
import UserDeleteCascadeDialog from '../../components/user-management/user-delete-cascade-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import { ProjectCell } from '../../components/common/project-cell';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [{ field: 'userId', header: 'Username', filter: true }];

// Deterministic avatar tint per user, so each row has a stable identity color.
// Drawn from the 4sCoreASTRUM brand family (blue → violet → magenta).
const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #1878C0, #104C87)',
  'linear-gradient(135deg, #2E86CE, #135FA8)',
  'linear-gradient(135deg, #6D4FB0, #4E3488)',
  'linear-gradient(135deg, #8A5FC4, #6D4FB0)',
  'linear-gradient(135deg, #A32FA6, #7A1F7C)',
  'linear-gradient(135deg, #C755C0, #A32FA6)',
];
const avatarGradient = (key: string) => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
};
const initialOf = (s?: string) => (s || '?').trim().charAt(0).toUpperCase() || '?';

/** Per-project status pills — reused in the expansion sub-rows. */
const ProjectStatus = ({ p }: { p: GroupedUserProject }) => (
  <div className="flex gap-1 flex-wrap">
    {p.blocked ? (
      <Tag value="Blocked" severity="danger" />
    ) : p.isRegistered ? (
      <Tag value="Registered" severity="success" />
    ) : (
      <Tag value="Pending" severity="warning" />
    )}
    {p.userStatus && <Tag value={p.userStatus} severity="info" />}
  </div>
);

const UserManagementList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({
    entity: 'userManagement',
    basePath: '/user-management',
  });
  const rrNavigate = useNavigate();
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.USER_MANAGEMENT) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.USER_MANAGEMENT);
  const canDelete = useCanDelete(JOB_NAMES.USER_MANAGEMENT);

  const [groups, setGroups] = useState<GroupedUser[]>([]);
  const [expandedRows, setExpandedRows] = useState<any>(null);
  const [personnelMap, setPersonnelMap] = useState<Map<string, PersonnelMinimal>>(new Map());
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const includeDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Pagination states for server-side pagination (paginated BY user, not by account row)
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting states
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  const debouncedSearchText = useDebounce(searchText, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 10000 });
  }, []);

  // Load personnel + accessible-project lookup maps once for resolving names.
  useEffect(() => {
    (async () => {
      try {
        const [personnel, accessible] = await Promise.all([
          personnelLookupService.getAll(),
          projectsService.getAccessible(),
        ]);
        setPersonnelMap(new Map(personnel.map((p) => [p._id, p])));
        setAccessibleProjects(accessible);
      } catch (err) {
        console.error('Failed to load lookup data:', err);
      }
    })();
  }, []);

  const fetchUsers = useCallback(
    async (
      page: number = 1,
      size: number = pageSize,
      sortBy: string | null = sortField,
      order: 1 | -1 | null = sortOrder,
      activeFilters: Record<string, any> = filters
    ) => {
      try {
        setLoading(true);
        const params: UserQueryParams = { page, page_size: size, include_deleted: includeDeleted };
        if (debouncedSearchText && debouncedSearchText.trim()) params.search = debouncedSearchText.trim();
        if (sortBy) {
          params.sort_by = sortBy;
          params.sort_order = order === 1 ? 'asc' : 'desc';
        }
        const filtersParam = buildColumnFiltersParam(activeFilters);
        if (filtersParam) params.filters = filtersParam;
        if (selectedProjectId) params.app_id = selectedProjectId;

        const response = await usersService.getGrouped(params);
        setGroups(response.data || []);
        setTotalRecords(response.pagination?.totalItems ?? (response.pagination as any)?.total ?? 0);
      } catch (err: any) {
        console.error('Failed to fetch users:', err);
        showError(extractErrorMessage(err, 'Failed to load users. Please try again.'));
      } finally {
        setLoading(false);
      }
    },
    [includeDeleted, debouncedSearchText, selectedProjectId, pageSize, sortField, sortOrder, filters, showError]
  );

  const handlePageChange = (event: any) => {
    setFirst(event.first);
    setPageSize(event.rows);
    fetchUsers(Math.floor(event.first / event.rows) + 1, event.rows, sortField, sortOrder);
  };

  const handleSort = (event: any) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder as 1 | -1 | null);
    setFirst(0);
    fetchUsers(1, pageSize, event.sortField, event.sortOrder);
  };

  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchUsers(1, pageSize, sortField, sortOrder, next);
  };

  useEffect(() => {
    setFirst(0);
    fetchUsers(1, pageSize);
  }, [includeDeleted, debouncedSearchText, selectedProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteClick = (id: string) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedId) return;
    try {
      setDeleteLoading(true);
      await usersService.delete(selectedId);
      setDeleteDialogOpen(false);
      setSelectedId(null);
      fetchUsers(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      setDeleteDialogOpen(false);
      setSelectedId(null);
      showError(extractErrorMessage(err, 'Failed to delete user'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedId(null);
  };

  // Person label (name for a linked personnel, else the account email).
  const personName = useCallback(
    (g: GroupedUser) => {
      if (g.personnelMasterId) {
        const p = personnelMap.get(g.personnelMasterId);
        if (p) return p.name;
      }
      return g.email || '—';
    },
    [personnelMap]
  );

  // Per-project row inside the expansion: project + username + status + actions.
  const rowExpansionTemplate = (g: GroupedUser) => (
    <div className="px-6 py-4 bg-gray-50/70 dark:bg-gray-900/30">
      <div className="flex items-center gap-2 mb-2.5">
        <i className="pi pi-folder-open text-xs text-ink-muted" />
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Accounts in {g.projectCount} {g.projectCount === 1 ? 'project' : 'projects'}
        </span>
      </div>
      <div className="ds-card overflow-hidden">
        {/* Column headers for the per-project sub-rows. */}
        <div className="flex items-center gap-4 px-4 py-2 bg-gray-100/70 dark:bg-gray-800/50 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
          <span className="w-64 shrink-0">Project</span>
          <span className="flex-1 min-w-0">Username</span>
          <span className="w-40 shrink-0">Status</span>
          <span className="w-28 shrink-0 text-right">Actions</span>
        </div>
        <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-800">
          {g.projects.map((p) => (
            <div key={p._id} className="flex items-center gap-4 px-4 py-2.5 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-colors">
              <div className="w-64 shrink-0">
                <ProjectCell appId={p.appId} projects={accessibleProjects} />
              </div>
              <div className="flex-1 min-w-0 text-sm text-ink truncate">{p.userId || '—'}</div>
              <div className="w-40 shrink-0"><ProjectStatus p={p} /></div>
              <div className="w-28 shrink-0 flex gap-1 justify-end">
                <button aria-label="View"
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => navigateToView(p._id)}
                  data-pr-tooltip="View"
                >
                  <i className="pi pi-eye text-green-600 dark:text-green-400" style={{ fontSize: '1.05rem' }} />
                </button>
                {canUpdate && (
                  <button aria-label="Edit"
                    className="p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    onClick={() => navigateToEdit(p._id)}
                    data-pr-tooltip="Edit"
                  >
                    <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.05rem' }} />
                  </button>
                )}
                {canDelete && (
                  <button aria-label="Delete"
                    className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    onClick={() => handleDeleteClick(p._id)}
                    data-pr-tooltip="Delete"
                  >
                    <i className="pi pi-trash text-red-600" style={{ fontSize: '1.05rem' }} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const columns: SaasColumn<GroupedUser>[] = useMemo(() => {
    return [
      {
        field: 'userId',
        header: 'Username',
        sortable: true,
        filter: true,
        hideable: false,
        style: { width: 220 },
        body: (g: GroupedUser) => (
          <div className="flex items-center gap-3">
            <span
              className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-sm ring-2 ring-white dark:ring-gray-800"
              style={{ background: avatarGradient(g.key || g.userId || '') }}
            >
              {initialOf(personName(g) !== '—' ? personName(g) : g.userId)}
            </span>
            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-ink truncate" title={g.userId || undefined}>{g.userId || '—'}</span>
              <span className="text-xs text-ink-muted">
                {g.projectCount} {g.projectCount === 1 ? 'project' : 'projects'}
              </span>
            </div>
          </div>
        ),
      },
      {
        field: 'email',
        header: 'Mail',
        sortable: false,
        style: { width: 240 },
        body: (g: GroupedUser) => (
          <div className="flex flex-col min-w-0">
            <span className="text-sm text-ink truncate" title={personName(g)}>{personName(g)}</span>
            {g.personnelMasterId && g.email && (
              <span className="text-xs text-ink-muted truncate" title={g.email}>{g.email}</span>
            )}
          </div>
        ),
      },
      {
        field: 'projects',
        header: 'Projects',
        sortable: false,
        style: { width: 200 },
        // Compact code chips on ONE line, the rest folded into "+N more".
        body: (g: GroupedUser) => {
          const shown = g.projects.slice(0, 2);
          return (
            <div className="flex flex-nowrap items-center gap-1 overflow-hidden">
              {shown.map((p) => {
                const proj = accessibleProjects.find((x) => x._id === p.appId);
                const label = proj?.appCode || proj?.name || 'Project';
                const full = proj ? (proj.appCode ? `${proj.name} (${proj.appCode})` : proj.name) : undefined;
                return (
                  <span
                    key={p._id}
                    title={full}
                    className="inline-flex max-w-[110px] items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-800"
                  >
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                    <span className="truncate">{label}</span>
                  </span>
                );
              })}
              {g.projectCount > shown.length && (
                <span className="shrink-0 whitespace-nowrap text-xs text-ink-muted">
                  +{g.projectCount - shown.length} more
                </span>
              )}
            </div>
          );
        },
      },
      {
        field: 'status',
        header: 'Status',
        sortable: false,
        style: { width: 150 },
        body: (g: GroupedUser) => {
          // A single-project user reads cleaner as a plain status (no "1").
          if (g.projectCount === 1) return <ProjectStatus p={g.projects[0]} />;
          const pending = Math.max(0, g.projectCount - g.registeredCount - g.blockedCount);
          return (
            <div className="flex gap-1 flex-wrap">
              {g.registeredCount > 0 && <Tag value={`${g.registeredCount} Registered`} severity="success" />}
              {pending > 0 && <Tag value={`${pending} Pending`} severity="warning" />}
              {g.blockedCount > 0 && <Tag value={`${g.blockedCount} Blocked`} severity="danger" />}
            </div>
          );
        },
      },
      {
        field: 'actions',
        header: 'Actions',
        align: 'center',
        hideable: false,
        sortable: false,
        style: { width: 120 },
        // Grouped View/Edit — one screen for the whole person (identity + projects).
        body: (g: GroupedUser) => {
          const repId = g.projects[0]?._id;
          if (!repId) return null;
          return (
            <div className="flex gap-1 justify-center">
              <button
                className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                onClick={() => rrNavigate(`/user-management/group/${repId}`)}
                data-pr-tooltip="View"
              >
                <i className="pi pi-eye text-green-600 dark:text-green-400" style={{ fontSize: '1.05rem' }} />
              </button>
              {canUpdate && (
                <button
                  className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  onClick={() => rrNavigate(`/user-management/group/${repId}/edit`)}
                  data-pr-tooltip="Edit"
                >
                  <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.05rem' }} />
                </button>
              )}
            </div>
          );
        },
      },
    ];
  }, [personName, accessibleProjects, canUpdate, rrNavigate]);

  return (
    <PermissionGuard jobName={JOB_NAMES.USER_MANAGEMENT}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-4 md:p-6'} data-testid="SCR-UserManagement-List">
        {!embedded && (
          <PageHeader
            icon="pi pi-users"
            title="User Master"
            subtitle="Users combined across projects — expand a row to manage each project account"
            testId="UserManagement.Header"
          />
        )}

        <div className={embedded ? '' : 'ds-card overflow-hidden'}>
          <SaasDataTable
            tableId="user-management-grouped"
            fitColumns
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={groups}
            loading={loading}
            dataKey="key"
            emptyMessage="No users found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchText}
            onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search users..."
            testId="UserManagementList"
            expandedRows={expandedRows}
            onRowToggle={(e: any) => setExpandedRows(e.data)}
            rowExpansionTemplate={rowExpansionTemplate}
            toolbarStart={
              <div className="flex items-center gap-2 flex-wrap">
                {!appId && accessibleProjects.length > 1 && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({
                      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                      value: p._id,
                    }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '160px', width: '160px' }}
                    data-testid="UserManagementList.Filter.Project"
                    resetFilterOnHide
                  />
                )}
              </div>
            }
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {totalRecords} {totalRecords === 1 ? 'user' : 'users'}
                </span>
                <RefreshButton
                  onClick={() => fetchUsers(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)}
                  loading={loading}
                />
                {canCreate && (
                  <Button
                    label="Add User"
                    icon="pi pi-plus"
                    onClick={() => navigateToCreate()}
                    testId="UserManagementList.Button.Create"
                    size="small"
                  />
                )}
              </>
            }
          />
        </div>

        {/* Delete dialog — previews the cascade (assignments, devices, sessions) before deleting */}
        <UserDeleteCascadeDialog
          userId={deleteDialogOpen ? selectedId : null}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          deleting={deleteLoading}
          testId="UserManagementList.Dialog.Delete"
        />
      </div>
    </PermissionGuard>
  );
};

export default UserManagementList;
